import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";
import { IMG } from "../data/content";
import { Check, ArrowRight, Car, Bike, HeartPulse, PiggyBank, GraduationCap, Briefcase } from "lucide-react";

const LUST_AUF = [
  "Kreativität am Arbeitsplatz",
  "Attraktive Karrierechancen",
  "Kundenkontakt",
  "Gestalterische Arbeit",
  "Arbeiten im Team",
  "Kommunikatives Miteinander",
  "Abwechslungsreiche Tätigkeiten",
];

const BRINGST_MIT = [
  "Handwerkliches Geschick",
  "Motivation, Kunden zu begeistern",
  "Kreative Ideen",
  "Einen Schulabschluss",
  "Begeisterung, mit natürlichen Werkstoffen zu arbeiten",
];

const GEHALT = [
  { jahr: "1. Lehrjahr", betrag: "1.000,- €" },
  { jahr: "2. Lehrjahr", betrag: "1.100,- €" },
  { jahr: "3. Lehrjahr", betrag: "1.200,- €" },
];

const EXTRAS = [
  { icon: Car, text: "Führerscheinzuschuss 1.000,- €" },
  { icon: Bike, text: "E-Bike-Leasing" },
  { icon: HeartPulse, text: "Private Kranken­zusatz­versicherung" },
  { icon: PiggyBank, text: "Betriebliche Altersvorsorge" },
];

const KARRIERE = ["Bauleiter", "Techniker", "Parkettlegermeister", "Restaurator", "Selbstständigkeit"];

const BIETEN_WIR = [
  "Arbeiten in einem jungen, aufgeschlossenen Team",
  "Weiterbildungsmöglichkeiten",
  "Übernahme nach abgeschlossener Lehre",
  "Vielseitige Arbeitsbereiche",
  "Moderne Maschinen",
  "Tägliche Erfolgserlebnisse",
];

const KarriereHero = () => (
  <section data-testid="karriere-hero" className="relative min-h-[68vh] flex items-end overflow-hidden">
    <img
      src={IMG.karriereHero}
      alt="Die Auszubildenden von Fussboden Blum mit den Geschäftsführern"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B30]/95 via-[#2B2B30]/55 to-[#2B2B30]/30" />
    <div className="container-blum relative z-10 pb-16 pt-40">
      <Reveal>
        <p data-testid="karriere-hero-script" className="font-script text-3xl lg:text-4xl text-[#D7C4B1]">
          Dein Start ins Handwerk
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mt-3 max-w-3xl">
          Leg Dir Deinen eigenen Boden!
        </h1>
        <p className="text-lg text-white/85 mt-5 max-w-2xl leading-relaxed">
          Ausbildung, Jobs und Karriere bei einem mehrfach ausgezeichneten
          Meisterbetrieb – mitten in Kalbach, mitten im Team.
        </p>
      </Reveal>
    </div>
  </section>
);

const Intro = () => (
  <section data-testid="karriere-intro" className="py-20 lg:py-28">
    <div className="container-blum max-w-3xl">
      <Reveal><p className="overline">Karriere bei Blum</p></Reveal>
      <Reveal delay={0.05}>
        <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
          Ausbildung, Jobs und Karriere bei Fussboden Blum
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="font-display text-2xl lg:text-3xl font-semibold text-[#2B2B30] leading-snug mt-8">
          Handwerk hat goldenen Boden …{" "}
          <span className="font-script text-[#D32F2F] font-normal text-3xl lg:text-4xl">
            und du kannst ihn dir legen!
          </span>
        </p>
      </Reveal>
    </div>
  </section>
);

const LustUndMitbringen = () => (
  <section data-testid="karriere-lust" className="pb-20 lg:pb-28">
    <div className="container-blum grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Reveal>
        <div className="bg-[#2B2B30] text-white p-8 lg:p-10 h-full">
          <h3 className="font-display text-xl lg:text-2xl font-bold">Du hast Lust auf:</h3>
          <ul className="mt-6 space-y-3.5">
            {LUST_AUF.map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center bg-[#D32F2F] shrink-0">
                  <Check className="w-3.5 h-3.5 text-white" />
                </span>
                <span className="text-white/85">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <div className="bg-[#F9F8F6] border border-[#E5E0D8] p-8 lg:p-10 h-full">
          <h3 className="font-display text-xl lg:text-2xl font-bold text-[#2B2B30]">Das bringst du mit:</h3>
          <ul className="mt-6 space-y-3.5">
            {BRINGST_MIT.map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center bg-[#D32F2F] shrink-0">
                  <Check className="w-3.5 h-3.5 text-white" />
                </span>
                <span className="text-[#4A4A52]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);

const Verguetung = () => (
  <section data-testid="karriere-verguetung" className="wood-band text-white py-20 lg:py-28">
    <div className="container-blum">
      <Reveal><p className="overline text-[#D7C4B1]">Du bekommst</p></Reveal>
      <Reveal delay={0.05}>
        <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
          Eine faire Ausbildungsvergütung – und mehr.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
        {GEHALT.map((g, i) => (
          <Reveal key={g.jahr} delay={i * 0.06}>
            <div data-testid={`gehalt-${i}`} className="bg-white/5 border border-white/10 p-8 text-center h-full">
              <p className="text-xs uppercase tracking-[0.18em] text-white/50 font-display">{g.jahr}</p>
              <p className="font-display text-4xl lg:text-5xl font-bold text-[#D7C4B1] mt-3">{g.betrag}</p>
              <p className="text-white/60 text-sm mt-1">pro Monat</p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        {EXTRAS.map((e, i) => (
          <Reveal key={e.text} delay={0.1 + i * 0.06}>
            <div className="flex items-center gap-4 bg-white/5 border border-white/10 p-5 h-full">
              <span className="w-10 h-10 flex items-center justify-center bg-[#D32F2F] shrink-0">
                <e.icon className="w-5 h-5 text-white" />
              </span>
              <span className="text-white/85 text-sm leading-snug min-w-0 hyphens-auto">{e.text}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WegeUndAngebot = () => (
  <section data-testid="karriere-wege" className="py-20 lg:py-28">
    <div className="container-blum">
      <div className="max-w-2xl">
        <Reveal><p className="overline">Karrierechancen</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
            Dein Weg nach der Lehre
          </h2>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="flex flex-wrap items-center gap-3 mt-10">
          {KARRIERE.map((k, i) => (
            <span key={k} className="flex items-center gap-3">
              <span
                data-testid={`karriere-weg-${i}`}
                className="font-display font-semibold text-[#2B2B30] border border-[#E5E0D8] bg-white px-5 py-3"
              >
                {k}
              </span>
              {i < KARRIERE.length - 1 && <ArrowRight className="w-5 h-5 text-[#D7C4B1]" aria-hidden="true" />}
            </span>
          ))}
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-16 lg:mt-20">
        <div>
          <Reveal><p className="overline">Das bieten wir</p></Reveal>
          <ul className="mt-8 space-y-3.5">
            {BIETEN_WIR.map((item, i) => (
              <Reveal key={item} delay={i * 0.04}>
                <li className="flex items-center gap-3">
                  <span className="w-6 h-6 flex items-center justify-center bg-[#D32F2F] shrink-0">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </span>
                  <span className="text-[#4A4A52]">{item}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
        <div>
          <Reveal><p className="overline">Tägliche Erfolgserlebnisse</p></Reveal>
          <Reveal delay={0.05}>
            <p className="text-[#6B6B73] text-lg leading-relaxed mt-8">
              Bevor der Boden gespachtelt wird, kannst du mit modernen Bodenstrippern alte Beläge
              einfach entfernen. Mit modernen Werkzeugen wird ein perfekter Unterboden erstellt.
              Darauf wird der neue Parkettboden perfekt verlegt und mit einem Pflegeöl zum Glänzen
              gebracht. Am Ende des Tages siehst du immer, was du geschafft hast. So hast du jeden
              Tag Erfolgserlebnisse und kannst stolz auf deine Arbeit sein.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-script text-3xl lg:text-4xl text-[#D32F2F] mt-8">
              Traue keinem Boden, den du nicht selbst gelegt hast!
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Ausbildungsbetrieb = () => (
  <section data-testid="karriere-ausbildungsbetrieb" className="py-20 lg:py-28 bg-white border-y border-[#E5E0D8]">
    <div className="container-blum max-w-3xl">
      <Reveal><p className="overline">Ausbildungsbetrieb</p></Reveal>
      <Reveal delay={0.05}>
        <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
          Wir bilden aus – eine Investition in die Zukunft!
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="text-[#6B6B73] text-lg leading-relaxed mt-6 space-y-5">
          <p>
            Der Begriff „Nachhaltigkeit“ hat im Meister- und Ausbildungsbetrieb Fußboden Blum einen
            besonderen Stellenwert – im Umgang mit Kunden und Geschäftspartnern, in der Qualität
            unserer Arbeit und vor allem in der Ausbildung junger Menschen. Wir bilden seit über 20
            Jahren regelmäßig Parkett- und Bodenleger sowie Bürokaufleute aus und sorgen so für
            qualifizierte Nachwuchskräfte. Hieraus sind bereits{" "}
            <strong className="text-[#2B2B30]">Kammersieger und ein Landessieger der Parkettinnung
            Hessen</strong>{" "}
            hervorgegangen.
          </p>
          <p>
            Wir bieten zukunftssichere Arbeitsplätze und pflegen ein respektvolles, familiäres und
            hilfsbereites Miteinander in einer stabilen Firmenstruktur. Die verlässliche und
            zielorientierte Führung unserer Mitarbeiterinnen und Mitarbeiter ist für uns
            selbstverständlich. Regelmäßige interne und externe Schulungen und Fortbildungen geben
            unseren Mitarbeiterinnen und Mitarbeitern Gelegenheit, ihre Kenntnisse aufzufrischen und
            zu erweitern.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const BERUFE = [
  {
    icon: GraduationCap,
    title: "Parkett- und Bodenleger (m/w)",
    text: "Als Auszubildende/r zum Parkett- oder Bodenleger (m/w) erwarten Sie eine spannende Ausbildung und ein abwechslungsreicher Beruf mit täglich wechselnden, kreativen Aufgaben. Dank unseres dynamischen Teams wird die Arbeit auf der Baustelle nie langweilig. Vielfältige Weiterbildungsmöglichkeiten – zum Beispiel zum Techniker, Meister oder Restaurator – eröffnen Ihnen interessante Zukunftsperspektiven.",
  },
  {
    icon: Briefcase,
    title: "Kaufmann/-frau für Büromanagement (m/w)",
    text: "Auch als Auszubildende/r zum Kaufmann/-frau für Büromanagement wartet eine abwechslungsreiche Tätigkeit mit mannigfachen Aufgabenbereichen auf Sie – dazu gehören neben „klassischer“ Büro-Arbeit auch Beratung und Verkauf. Sie übernehmen Verantwortung im Betrieb, indem Sie steten Kontakt zu unseren Kunden halten und für verschiedene Aufgabenbereiche zuständig sind.",
  },
];

const Berufe = () => (
  <section data-testid="karriere-berufe" className="py-20 lg:py-28">
    <div className="container-blum">
      <div className="max-w-2xl mb-12">
        <Reveal><p className="overline">Unsere Ausbildungsberufe</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
            Zwei Wege, ein Ziel: Dein Handwerk.
          </h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {BERUFE.map((b, i) => (
          <Reveal key={b.title} delay={i * 0.08}>
            <div data-testid={`beruf-${i}`} className="bg-[#F9F8F6] border border-[#E5E0D8] p-8 lg:p-10 h-full">
              <span className="w-12 h-12 flex items-center justify-center bg-[#D32F2F]">
                <b.icon className="w-6 h-6 text-white" />
              </span>
              <h3 className="font-display text-xl lg:text-2xl font-bold text-[#2B2B30] mt-5">{b.title}</h3>
              <p className="text-[#6B6B73] leading-relaxed mt-3">{b.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="border-l-2 border-[#D32F2F] bg-[#F9F8F6] p-6 mt-6 text-[#4A4A52] leading-relaxed">
          Zusätzlich zu diesen klassischen Berufsausbildungen bieten wir in beiden Bereichen auch{" "}
          <strong className="text-[#2B2B30]">Ferien- und Jahrespraktika für Schüler</strong> an –
          eine hervorragende Gelegenheit, die Berufe und unser Unternehmen näher kennenzulernen.
        </p>
      </Reveal>
    </div>
  </section>
);

const BewerbungCTA = () => (
  <section data-testid="karriere-cta" className="wood-band text-white py-20 lg:py-28">
    <div className="container-blum max-w-3xl text-center">
      <Reveal>
        <p className="font-script text-3xl text-[#D7C4B1]">Haben wir Ihr Interesse geweckt?</p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
          Nehmen Sie Kontakt mit uns auf
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-white/80 text-lg leading-relaxed mt-5">
          Wir freuen uns auf Ihre aussagekräftigen Bewerbungsunterlagen!
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <Link to="/#kontakt" data-testid="karriere-cta-button" className="btn-base btn-red">
            Jetzt bewerben <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="mailto:info@fussboden-blum.de" className="btn-base border border-white/30 text-white hover:bg-white/10 transition-colors">
            info@fussboden-blum.de
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Karriere() {
  useSeo(
    "Ausbildung, Jobs und Karriere bei Fussboden Blum",
    "Ausbildung, Jobs und Karriere bei Fussboden Blum in Kalbach"
  );

  return (
    <div data-testid="karriere-page">
      <Navbar />
      <main>
        <KarriereHero />
        <Intro />
        <LustUndMitbringen />
        <Verguetung />
        <WegeUndAngebot />
        <Ausbildungsbetrieb />
        <Berufe />
        <BewerbungCTA />
      </main>
      <Footer />
    </div>
  );
}
